import React, { useEffect } from 'react'
import { domToReact } from 'html-react-parser';
import parse from 'html-react-parser';
import animateScrollTo from 'animated-scroll-to';
import AnimatedLink from '../components/global/animatedLink';
import filterObject from 'filter-obj';
import IframeResizer from 'iframe-resizer-react'

export interface ParseHTMLOptions {
  // Render relative anchors as plain <a> tags instead of AnimatedLink.
  // AnimatedLink renders a TransitionLink, which reads context that
  // gatsby-plugin-transition-link only provides at page level. Content rendered
  // above that - the cookie banner mounts from wrapRootElement - would otherwise
  // throw "Cannot destructure property 'disableAnimation' of '_ref2'".
  plainRelativeLinks?: boolean;
}

export const parseHTML = (html: string, options: ParseHTMLOptions = {}) =>
  parse(html, replaceHTML(options));

// Pull the fragment id out of a hash anchor href (e.g. "#foo" or "/page/#foo")
// so we can treat it as an in-page scroll target.
const getHashId = (href?: string): string | null => {
  if (!href) return null;
  const hashIndex = href.indexOf('#');
  if (hashIndex === -1) return null;
  const id = href.slice(hashIndex + 1);
  return id ? decodeURIComponent(id) : null;
};

// Scroll relative to the current page instead of triggering a fresh load.
// Mirrors the anchorLink behaviour in rich-text-block: if the target exists on
// this page we scroll to it and sync the URL hash without a reload, otherwise we
// let the href navigate normally.
const handleHashAnchorClick = (evt: React.MouseEvent<HTMLAnchorElement>, hashId: string) => {
  if (typeof document === 'undefined') return;
  const target = document.getElementById(hashId);
  if (!target) return;
  evt.preventDefault();
  animateScrollTo(target.offsetTop, { speed: 800 });
  if (typeof window !== 'undefined' && window.history) {
    window.history.pushState(null, '', `${window.location.pathname}${window.location.search}#${hashId}`);
  }
};

const replaceHTML = ({ plainRelativeLinks = false }: ParseHTMLOptions) => ({
  replace: domNode => {
    // Filter out Office XML namespace tags (o:p, w:p, w:t, etc.) that come from Word documents
    // Check and handle namespace tags first, before any other processing
    if (domNode && domNode.name && domNode.name.includes(':')) {
      // Return only the children of namespace tags, stripping the tag itself
      if (domNode.children && Array.isArray(domNode.children)) {
        return domToReact(domNode.children);
      }
      // If no children, return nothing to skip the tag entirely
      return null;
    }

    if (domNode && domNode.attribs) {
      // Clear the HTML of a lot of extra garbage styles that get added in the CMS
      let acceptableAttributes = [
        "class",
        "id",
        "width",
        "height",
        "href",
        "start",
        'src',
        'Source',
        'onClick',
        'secondParseAnchor',
        'target',
        'rel',
        'title',
      ];
      const newAttrs = filterObject(domNode.attribs, key => acceptableAttributes.includes(key as string));
      
      domNode.attribs = newAttrs;

      switch (domNode.name) {
        case 'a':
          // If linking directly to online banking - setup the correct onclick event
          if (domNode.attribs.href === 'https://ib.bankvic.com.au/bvib/signon/login.aspx') {
            return React.createElement('span',
              {
                className: 'login-anchor',
                onClick: (e) => {
                  e.preventDefault()
                  var newWindow = window.open("https://ib.bankvic.com.au/bvib/signon/login.aspx", 'MVP3', "toolbar=yes,menubar=no,location=no,hotkeys=no,directories=no,scrollbars=yes,resizable=yes,status=yes,personalbar=no,self.resizeTo(screen.availWidth,screen.availHeight)");
                  newWindow?.focus();
                },
                children: domToReact(domNode.children)
              }
            );
          }
          // In-page hash anchors scroll relative to the current page rather
          // than doing a jump/reload.
          const hashId = getHashId(domNode.attribs.href);
          if (hashId) {
            return (
              <a
                href={domNode.attribs.href}
                className="rte-item"
                onClick={(e) => handleHashAnchorClick(e, hashId)}
              >
                {domToReact(domNode.children)}
              </a>
            )
          }
          // Relative anchors use animatedLink
          if (
            !/^http:\/\/|^https:\/\/|^tel|^mailto/.test(domNode.attribs.href) &&
            domNode.attribs.href !== 'get-help/?section=branches-and-atms-get-help-landing'
          ) {
            // A route transition needs page-level context, which content
            // rendered above wrapPageElement does not have.
            if (plainRelativeLinks) {
              return (
                <a href={domNode.attribs.href} className="internal-link rte-item">
                  {domToReact(domNode.children)}
                </a>
              )
            }
            return (
              <AnimatedLink to={domNode.attribs.href} className={`internal-link rte-item`}>
                {domNode.children[0].data}
              </AnimatedLink>
            )
          }
          break;

        case 'iframe':
          // let youtubeID = YouTubeGetID(domNode.attribs.src);
          // let thumbnail = `https://img.youtube.com/vi/${youtubeID}/0.jpg`;
          let isVideo = domNode.attribs.src.indexOf('youtube') > 0 || domNode.attribs.src.indexOf('vimeo') > 0
          const iframeClass = isVideo ? 'video-container rte-item' : 'iframe-container rte-item'
          if (isVideo) {

            return React.createElement('div',
              { className: iframeClass },
              React.createElement('iframe', domNode.attribs)
            )
          } else {
            return (
              <div className="iframe-container">
                <IframeResizer
                  src={domNode.attribs.src}
                  style={{ width: '1px', minWidth: '100%' }}
                  log={true}
                  checkOrigin={false}
                  autoResize={true}
                  onResized={() => { }}
                />
              </div>
            )
          }

        case 'table':
          return (
            <div className="table-wrap rte-item">
              <table>
                {domToReact(domNode.children)}
              </table>
            </div>
          )

        case 'img':
          let src = '';
          if (!domNode.attribs.src.includes('?fit=clip&w=813')) {
            src = domNode.attribs.src + '?fit=clip&w=813';
          } else {
            src = domNode.attribs.src;
          }
          return React.createElement(
            'img',
            {
              className: 'content-image rte-item',
              src: src,
              width: domNode.attribs.width,
              height: domNode.attribs.height
            }
          );

        case 'script':
          return

        default:
          return domNode
      }
    }
  }
});